import React, { useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import LoadingButton from 'components/common/LoadingButton';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormProvider,
  RHFControl,
  RHFDatePicker,
  RHFDocumentUpload,
  RHFSelect
} from 'components/common/form';
import Flex from 'components/common/Flex';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const schema = Yup.object().shape({
  date: Yup.string().required('Required Field'),
  mosque: Yup.string().required('Required Field'),
  reporter: Yup.string(),
  comment: Yup.string(),
  reportDocument: Yup.mixed(),
  report: Yup.object()
    .shape({
      name: Yup.string(),
      url: Yup.string()
    })
    .nullable()
});

function AddReportModal({
  show,
  onHide,
  title,
  loading,
  onSubmit,
  initValues,
  mosqueOptions
}) {
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      date: format(new Date(), 'yyyy-MM-dd'),
      mosque: '',
      reporter: '',
      comment: '',
      reportDocument: [],
      editReport: null
    },
    resolver: yupResolver(schema)
  });

  const { setValue, handleSubmit, reset, watch } = methods;
  const reportDocument = watch('reportDocument');
  const editReport = watch('editReport');

  useEffect(() => {
    if (initValues) {
      reset({
        date: initValues.report_date,
        mosque: initValues.mosque_id,
        reporter: initValues.reporter || '',
        comment: initValues.comment || '',
        reportDocument: [],
        editReport: initValues.report_document
          ? {
              name: initValues.report_name,
              url: initValues.report_document
            }
          : null
      });
    }
  }, [initValues, reset]);

  return (
    <>
      <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton className="modal-header">
          <Modal.Title className="modal-header-title">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-3 ps-3 pe-3">
          <FormProvider
            methods={methods}
            onSubmit={handleSubmit(onSubmit)}
            loading={loading}
          >
            <Flex gap="2" direction="column" className="mt-3">
              <RHFDatePicker name="date" label="Date" required />
              <RHFSelect
                label={'Mosque'}
                name={'mosque'}
                options={mosqueOptions}
                required
              />
              <RHFControl
                label={'Reporter'}
                name="reporter"
                placeholder="Reporter name"
                type="text"
              />
              <RHFControl
                label={'Comment'}
                name="comment"
                placeholder="Comment"
                type="text"
                as="textarea"
                rows={5}
              />
              <RHFDocumentUpload
                label="Report PDF"
                accept=".pdf"
                name="reportDocument"
                multiple={false}
                maxFiles={1}
                maxSizeMB={10}
              />
              {editReport &&
                (!reportDocument || reportDocument.length === 0) && (
                  <div>
                    <a
                      href={editReport.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon="link" />
                      <span className="ms-2">{editReport.name}</span>
                    </a>
                    <Button
                      variant="danger"
                      size="sm"
                      className="ms-2"
                      onClick={() => setValue('editReport', null)}
                    >
                      delete
                    </Button>
                  </div>
                )}
              <LoadingButton
                style={{ width: '100%', color: 'white' }}
                variant="primary"
                loading={loading}
                disabled={loading}
                type={'submit'}
              >
                {initValues ? 'Edit' : 'Save'}
              </LoadingButton>
            </Flex>
          </FormProvider>
        </Modal.Body>
      </Modal>
    </>
  );
}

AddReportModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  title: PropTypes.string,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  mosqueOptions: PropTypes.array,
  initValues: PropTypes.object
};

export default AddReportModal;
